import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Info} from 'lucide-react';
import {FORECAST,KPI_GROUP,KPI_GROUP_ORDER,KPI_THRESHOLDS,KPI_WINDOW_DAYS,PI_STAGES,type KpiId,type KpiMetric,type PiProgressRow,vnDateTime,vnIso} from '@hisense/shared';
import {useKpis,usePiProgress} from '@/api/kpis';
import {useKpiUnit} from '@/lib/kpiUnit';
import {PageBody,PageHeader} from '@/components/ui/PageHeader';
import {Button} from '@/components/ui/Button';
import {LoadingState,ErrorState} from '@/components/ui/States';
import {usePageCrumbs} from '@/components/shell/breadcrumb';
import {ExportButton} from '@/components/domain/ExportButton';
/**
 * Định nghĩa đã chốt 16/09/2026 (thay cho khối "chờ khách xác nhận"): cửa sổ,
 * ngưỡng cảnh báo và phương pháp dự báo — để người đọc số biết số được tính thế nào.
 */
function Definitions(){
 const {t}=useTranslation();
 return <details className="mb-4 rounded-sm border border-line bg-sunken/40 p-2 text-xs"><summary className="flex cursor-pointer items-center gap-2 font-medium"><Info className="size-4 shrink-0 text-brand" aria-hidden/>{t('kpis.definitions.title')}</summary>
  <ul className="mt-2 space-y-1 text-ink-soft">
   <li>{t('kpis.definitions.window',{days:KPI_WINDOW_DAYS})}</li>
   <li>{t('kpis.definitions.thresholds',{otd:KPI_THRESHOLDS.delivery_on_time,supplier:KPI_THRESHOLDS.supplier_on_time,iqc:KPI_THRESHOLDS.qc_iqc_acceptance})}</li>
   <li>{t('kpis.definitions.forecast',{window:FORECAST.window,history:FORECAST.minHistory,backtest:FORECAST.minBacktest})}</li>
   <li>{t('kpis.definitions.scope')}</li>
  </ul></details>;
}
function Tile({metric}:{metric:KpiMetric}){
 const {t}=useTranslation();const unitLabel=useKpiUnit();
 const label=t(`kpis.${metric.id as KpiId}`);
 return <article className="rounded border border-line bg-surface p-4"><h2 className="text-sm font-semibold">{label}</h2>
  <p className="mt-2 font-mono text-xl tabular">{metric.status==='present'?`${metric.value??'—'} ${unitLabel(metric.unit)}`.trim():t('kpis.missing')}</p>
  {metric.missingReason&&<p className="mt-1 text-xs text-warn">{t(`kpis.${metric.missingReason}`)}</p>}
  <p className="mt-2 text-xs text-ink-soft">{t(`kpis.about.${metric.id as KpiId}`)}</p>
  <p className="mt-2 text-2xs text-ink-soft">{t('kpis.asOf')} · <time className="font-mono" dateTime={vnIso(metric.asOf)}>{vnDateTime(metric.asOf)}</time></p>
  <details className="mt-2 text-2xs text-ink-soft"><summary className="cursor-pointer font-medium">{t('kpis.technical')}</summary>
   <p className="mt-1"><span className="font-medium">{t('kpis.source')}</span> <span className="font-mono">{metric.source}</span></p>
   <p className="mt-1"><span className="font-medium">{t('kpis.formula')}</span> <span className="font-mono">{metric.formula}</span></p>
  </details>
  {metric.drillDown.length>0&&<ul className="mt-3 max-h-40 overflow-auto text-2xs">{metric.drillDown.slice(0,12).map(row=><li key={row.id} className="flex justify-between gap-2 border-t border-line py-1"><Link className="truncate text-brand" to={row.href}>{row.label}</Link><span className="font-mono tabular">{row.quantity} {unitLabel(row.unit)}</span></li>)}</ul>}
 </article>;
}
/**
 * PI đang đi tới chứng từ nào.
 *
 * Liệt kê TỪNG mốc chứ không chỉ mốc cuối: nhiều PI xuất hóa đơn mà không có
 * đơn bán hàng hay phiếu đóng gói, và chỗ trống ở giữa mới là thứ người theo
 * dõi cần thấy. Không có số tiền nào trên bảng này — chỉ trạng thái thu.
 */
function ProgressRow({row}:{row:PiProgressRow}){
 const {t}=useTranslation();
 const done:Record<string,boolean>={created:true,ordered:row.hasSalesOrder,packed:row.hasPacking,invoiced:row.hasInvoice,delivered:row.delivered,settled:row.settlement==='paid'};
 return <tr className="border-t border-line">
  <td className="p-2"><Link className="font-mono text-brand" to={`/proforma-invoices/${row.piId}`}>{row.piNo}</Link></td>
  <td className="p-2">{row.customerName??'—'}</td>
  <td className="p-2">{t(`kpis.stages.${row.stage}`)}</td>
  <td className="p-2"><ul className="flex flex-wrap gap-1">{PI_STAGES.filter(k=>k!=='settled').map(k=>
   <li key={k} className={`rounded-xs border px-1.5 py-0.5 text-2xs ${done[k]?'border-brand/30 bg-brand-tint text-brand-ink':'border-line text-ink-faint'}`}>{t(`kpis.stages.${k}`)}</li>)}</ul></td>
  <td className="p-2">{t(`kpis.settlements.${row.settlement}`)}</td>
 </tr>;
}
function PiProgressPanel(){
 const {t}=useTranslation();const q=usePiProgress();
 if(q.isLoading)return <LoadingState/>;
 if(q.error||!q.data)return <ErrorState message={t('kpis.error')} onRetry={()=>void q.refetch()}/>;
 return <section className="mt-6">
  <div className="flex flex-wrap items-start justify-between gap-3">
   <div>
    <h2 className="font-semibold">{t('kpis.progress')}</h2>
    <p className="mt-1 text-xs text-ink-soft">{t('kpis.progressHint')}</p>
   </div>
   <ExportButton<PiProgressRow>
    name="pi-progress"
    title={t('kpis.progress')}
    rows={q.data?.data??[]}
    disabled={q.isFetching}
    errorKey="kpis.error"
    rowKey={r=>r.piId}
    columns={[
     {header:t('kpis.piNo'),value:r=>r.piNo},
     {header:t('kpis.customer'),value:r=>r.customerName},
     {header:t('kpis.stage'),value:r=>t(`kpis.stages.${r.stage}`)},
     // MỘT CỘT CHO MỖI MỐC, không gộp thành một chuỗi: chỗ TRỐNG ở giữa mới
     // là thứ người theo dõi cần lọc — "PI nào đã xuất hóa đơn mà chưa có
     // phiếu đóng gói" phải trả lời được bằng một bộ lọc Excel.
     {header:t('kpis.stages.ordered'),value:r=>t(r.hasSalesOrder?'common.yes':'common.no')},
     {header:t('kpis.stages.packed'),value:r=>t(r.hasPacking?'common.yes':'common.no')},
     {header:t('kpis.stages.invoiced'),value:r=>t(r.hasInvoice?'common.yes':'common.no')},
     {header:t('kpis.stages.delivered'),value:r=>t(r.delivered?'common.yes':'common.no')},
     {header:t('kpis.settlement'),value:r=>t(`kpis.settlements.${r.settlement}`)},
    ]}
   />
  </div>
  {q.data.data.length===0?<p className="mt-3 text-sm text-ink-soft">{t('kpis.progressEmpty')}</p>
   :<div className="mt-3 overflow-x-auto rounded border border-line bg-surface"><table className="w-full min-w-[720px] text-sm">
    <thead className="bg-sunken text-left text-xs"><tr>
     <th className="p-2">{t('kpis.piNo')}</th><th className="p-2">{t('kpis.customer')}</th>
     <th className="p-2">{t('kpis.stage')}</th><th className="p-2">{t('kpis.documents')}</th><th className="p-2">{t('kpis.settlement')}</th>
    </tr></thead>
    <tbody>{q.data.data.map(r=><ProgressRow key={r.piId} row={r}/>)}</tbody>
   </table></div>}
 </section>;
}
export function KpisPage(){
 const {t}=useTranslation();usePageCrumbs([{label:t('kpis.title')}]);
 const q=useKpis();
 return <PageBody><PageHeader title={t('kpis.title')} subtitle={t('kpis.intro')} actions={<Button size="sm" variant="secondary" onClick={()=>void q.refetch()}>{t('kpis.reload')}</Button>}/>
  <div className="my-4 flex flex-wrap items-center gap-2">
   {/*
     Chỉ số nào KHÔNG tính được cũng phải có mặt trong tệp, kèm lý do. Bỏ chúng
     đi thì người đọc tưởng hệ thống đo được mọi thứ; giữ lại thì thấy ngay còn
     thiếu dữ liệu ở đâu — đó mới là thứ đáng mang ra họp.
   */}
   <ExportButton<KpiMetric>
    name="kpis"
    title={t('kpis.title')}
    rows={q.data?.data.metrics??[]}
    disabled={q.isFetching}
    errorKey="kpis.error"
    columns={[
     {header:t('kpis.title'),value:m=>t(`kpis.${m.id as KpiId}`)},
     {header:t('common.status'),value:m=>t(m.status==='present'?'common.yes':'kpis.missing')},
     {header:t('common.total'),value:m=>m.value},
     {header:t('kpis.unit'),value:m=>m.unit},
     {header:t('kpis.missing'),value:m=>m.missingReason?t(`kpis.${m.missingReason}`):null},
     {header:t('kpis.asOf'),value:m=>vnDateTime(m.asOf)},
     {header:t('kpis.source'),value:m=>m.source},
     {header:t('kpis.formula'),value:m=>m.formula},
    ]}
   /></div>
  <Definitions/>
  {q.isLoading?<LoadingState/>:q.error||!q.data?<ErrorState message={t('kpis.error')} onRetry={()=>void q.refetch()}/>:<>
   {q.data.data.alerts.length>0&&<section className="mb-4 rounded border border-warn/30 bg-warn-tint p-3 text-sm"><h2 className="font-medium">{t('kpis.alerts')}</h2><ul className="mt-2 space-y-1">{q.data.data.alerts.map(a=><li key={a.id}>{t(`kpis.${a.message}`)}</li>)}</ul></section>}
   {KPI_GROUP_ORDER.map(group=>{const rows=q.data!.data.metrics.filter(m=>KPI_GROUP[m.id]===group);if(!rows.length)return null;return <section key={group} className="mb-5"><h2 className="eyebrow mb-2 border-b border-line pb-1.5 text-ink-faint">{t(`kpis.groups.${group}`)}</h2><div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{rows.map(m=><Tile key={m.id} metric={m}/>)}</div></section>;})}
   <PiProgressPanel/>
  </>}
 </PageBody>;
}
